import {
  ENROLLMENT_TOKEN_DEFAULT_TTL_DAYS,
  ENROLLMENT_TOKEN_MAX_TTL_DAYS,
  ENROLLMENT_TOKEN_MAX_USES,
  type CreatedEnrollmentToken,
  type EnrollmentTokenSummary,
} from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode, type SyntheticEvent } from 'react';
import {
  createEnrollmentToken,
  listEnrollmentTokens,
  revokeEnrollmentToken,
} from '../api/enrollment-tokens.js';
import { listServerGroups } from '../api/servers.js';
import { needsReauth } from '../api/client.js';
import { useReauth } from '../auth/reauth-context.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageHeader } from '../components/PageHeader.js';
import { TokenStateBadge } from '../components/StatusBadge.js';
import { effectiveTimezone, formatInstant } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField, TextField } from '../ui/Field.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * Enrollment tokens (knowledge doc section 10, method A).
 *
 * The token is shown **once**, in a panel this page holds in component state. It
 * is never written to the query cache, to storage or to the URL — navigating away
 * loses it, which is the behaviour, not a limitation.
 */

const NO_GROUP = '__none__';

export function EnrollmentTokensPage(): ReactNode {
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const queryClient = useQueryClient();

  const [created, setCreated] = useState<CreatedEnrollmentToken | null>(null);
  const [activeOnly, setActiveOnly] = useState(false);

  const tokens = useQuery({
    queryKey: ['enrollment-tokens', activeOnly],
    queryFn: () => listEnrollmentTokens({ activeOnly }),
  });

  const afterChange = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['enrollment-tokens'] });
  };

  return (
    <div className="page">
      <PageHeader
        title="Enrollment tokens"
        description="How a new server joins the fleet. A token only lets a machine in; its own key pair is what it uses afterwards."
      />

      {created !== null && (
        <ShowOncePanel
          created={created}
          onDismiss={() => {
            setCreated(null);
          }}
        />
      )}

      <CreateTokenCard
        onCreated={(token) => {
          setCreated(token);
          afterChange();
        }}
      />

      <Card
        title="Existing tokens"
        aside={
          <label className="checkbox">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(event) => {
                setActiveOnly(event.target.checked);
              }}
            />
            Only ones that still work
          </label>
        }
      >
        {tokens.isPending && <Loading />}

        {tokens.isError && (
          <Alert tone="danger" title="Could not load the tokens">
            Try again in a moment.
          </Alert>
        )}

        {tokens.data?.items.length === 0 && (
          <EmptyState title="No tokens yet">
            Create one above, then run the installer on a server with it.
          </EmptyState>
        )}

        {tokens.data !== undefined && tokens.data.items.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <caption className="visually-hidden">Enrollment tokens and their state</caption>
              <thead>
                <tr>
                  <th scope="col">State</th>
                  <th scope="col">Uses</th>
                  <th scope="col">Group</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Created by</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tokens.data.items.map((token) => (
                  <TokenRow
                    key={token.id}
                    token={token}
                    timezone={timezone}
                    onRevoked={afterChange}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/**
 * Section 10: "The token is shown **once**; the API stores only its hash." So the
 * panel says so plainly rather than leaving someone to discover it by reloading.
 */
function ShowOncePanel({
  created,
  onDismiss,
}: {
  created: CreatedEnrollmentToken;
  onDismiss: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);

  const command = `sudo BZ_TOKEN_FILE=/root/bartizo.token ./install.sh agent --api ${window.location.origin}`;

  return (
    <Card
      title="Your new token"
      description="Copy it now. It is stored only as a hash, so this is the only time it can be shown."
      aside={<span className="badge badge-accent">Shown once</span>}
    >
      <code className="secret-text">{created.token}</code>

      <div className="row">
        <Button
          variant="primary"
          onClick={() => {
            void navigator.clipboard
              .writeText(created.token)
              .then(() => {
                setCopied(true);
              })
              .catch(() => {
                // Some browsers refuse without a secure context; the value is on
                // screen and selectable either way.
                setCopied(false);
              });
          }}
        >
          {copied ? 'Copied' : 'Copy token'}
        </Button>
        <Button onClick={onDismiss}>I have saved it</Button>
      </div>

      <div>
        <p className="text-sm">Then, on the server:</p>
        <code className="secret-text">{command}</code>
        <p className="muted text-xs">
          Write the token to that file first. Section 9: a secret goes through a file or stdin,
          never on a command line, where <code>ps</code> and shell history both keep it.
        </p>
      </div>
    </Card>
  );
}

function CreateTokenCard({
  onCreated,
}: {
  onCreated: (token: CreatedEnrollmentToken) => void;
}): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const groups = useQuery({ queryKey: ['server-groups'], queryFn: listServerGroups });

  const [expiresInDays, setExpiresInDays] = useState(ENROLLMENT_TOKEN_DEFAULT_TTL_DAYS);
  const [maxUses, setMaxUses] = useState(1);
  const [groupId, setGroupId] = useState(NO_GROUP);
  const [approvalMode, setApprovalMode] = useState<'auto_approve' | 'needs_approval'>(
    'needs_approval',
  );
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createEnrollmentToken({
        expiresInDays,
        maxUses,
        groupId: groupId === NO_GROUP ? null : groupId,
        labels: {},
        approvalMode,
      }),
    onSuccess: onCreated,
    onError: (caught: unknown) => {
      setError(
        needsReauth(caught)
          ? 'That needed a fresh confirmation. Try again.'
          : caught instanceof Error
            ? caught.message
            : 'Could not create the token.',
      );
    },
  });

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    // Section 12 rates managing tokens High risk, so the credential is asked for
    // before the request rather than after the API refuses it.
    if (await ensureReauthenticated()) create.mutate();
  };

  return (
    <Card
      title="Create a token"
      description="Section 12 rates this high risk, so it asks you to confirm first."
    >
      <form onSubmit={(event) => void submit(event)} className="stack">
        <div className="filters">
          <TextField
            label="Valid for (days)"
            type="number"
            min={1}
            max={ENROLLMENT_TOKEN_MAX_TTL_DAYS}
            value={String(expiresInDays)}
            onChange={(event) => {
              setExpiresInDays(Number(event.target.value));
            }}
          />
          <TextField
            label="Maximum servers"
            hint="How many machines may use it."
            type="number"
            min={1}
            max={ENROLLMENT_TOKEN_MAX_USES}
            value={String(maxUses)}
            onChange={(event) => {
              setMaxUses(Number(event.target.value));
            }}
          />
          <SelectField
            label="Group"
            value={groupId}
            onChange={(event) => {
              setGroupId(event.target.value);
            }}
          >
            <option value={NO_GROUP}>No group</option>
            {(groups.data ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </SelectField>
        </div>

        <SelectField
          label="When a server uses it"
          hint="Auto-approve lets anyone holding the token put a server into the fleet with nobody looking."
          value={approvalMode}
          onChange={(event) => {
            setApprovalMode(
              event.target.value === 'auto_approve' ? 'auto_approve' : 'needs_approval',
            );
          }}
        >
          <option value="needs_approval">Wait for an administrator to approve it</option>
          <option value="auto_approve">Let it in straight away</option>
        </SelectField>

        {error !== null && <Alert tone="danger">{error}</Alert>}

        <div className="row">
          <Button type="submit" variant="primary" busy={create.isPending} busyLabel="Creating…">
            Create token
          </Button>
        </div>
      </form>
    </Card>
  );
}

function TokenRow({
  token,
  timezone,
  onRevoked,
}: {
  token: EnrollmentTokenSummary;
  timezone: string;
  onRevoked: () => void;
}): ReactNode {
  const { ensureReauthenticated } = useReauth();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const revoke = useMutation({
    mutationFn: () => revokeEnrollmentToken(token.id),
    onSuccess: () => {
      setConfirming(false);
      onRevoked();
    },
    onError: (caught: unknown) => {
      setError(caught instanceof Error ? caught.message : 'Could not revoke it.');
    },
  });

  const start = async (): Promise<void> => {
    setError(null);
    if (await ensureReauthenticated()) setConfirming(true);
  };

  return (
    <tr>
      <td>
        <TokenStateBadge state={token.state} />
      </td>
      <td className="text-sm">
        {token.usedCount} of {token.maxUses}
      </td>
      <td className="text-sm">{token.group?.name ?? <span className="muted">—</span>}</td>
      <td className="text-sm">{formatInstant(token.expiresAt, { timezone })}</td>
      <td className="text-sm">{token.createdByEmail ?? <span className="muted">—</span>}</td>
      <td>
        {token.state !== 'revoked' && (
          <Button variant="ghost" onClick={() => void start()}>
            Revoke
          </Button>
        )}
        {confirming && (
          <ConfirmDialog
            title="Revoke this token?"
            description="Servers that already joined with it keep working — their key pair is what they use now."
            confirmLabel="Revoke it"
            danger
            busy={revoke.isPending}
            error={error}
            onConfirm={() => {
              revoke.mutate();
            }}
            onCancel={() => {
              setConfirming(false);
            }}
          />
        )}
      </td>
    </tr>
  );
}
